/**
 * @file customer-consents.ts
 * @description «Согласия» клиента (phase19.16, партия «Юридические
 * документы», шаг Б; uiux-legal-steps-b-c §3; ФЗ-152): разбор ответа
 * согласий по видам, подписи вида, источника и редакции документа.
 *
 * Ответ шага Б — массив видов: поля последней записи на верхнем уровне +
 * `kind`, `active`, `history[]`. До выкладки нового API
 * сервер отдаёт плоский список записей — `groupConsents` раскладывает его по
 * видам сам (новые сверху), чтобы раздел работал в обоих случаях.
 */

import type { ConsentKindDto, ConsentKindGroupDto, ConsentRecordDto, ConsentSource, ConsentType } from "./api-customers";
import { formatShopDate } from "./shop-time";

/** Вид согласия словами. */
export const CONSENT_KIND_LABEL: Readonly<Record<ConsentType, string>> = {
  PD_PROCESSING: "Обработка персональных данных",
  MARKETING_EMAIL: "Рассылка по email",
  MARKETING_SMS: "Рассылка по SMS",
  COOKIE: "Cookie",
  // v0.19.6 — согласия из ЛК (origin=PORTAL)
  PERSONAL_DATA: "Обработка персональных данных",
  MARKETING_TELEGRAM: "Рассылка в Telegram",
  MARKETING_CHAT: "Рассылка в чате",
  CHAT_HISTORY: "История чата",
};

/** Где дано согласие (§3: «при регистрации», «при оформлении заказа»…). */
export const CONSENT_SOURCE_LABEL: Readonly<Record<ConsentSource, string>> = {
  REGISTRATION: "при регистрации",
  CHECKOUT: "при оформлении заказа",
  SETTINGS: "в настройках",
  IMPORT: "импорт",
  PORTAL: "в личном кабинете",
};

/** Рассылки: отдельного документа нет — согласие записано по общей политике. */
const MARKETING: ReadonlySet<ConsentType> = new Set(["MARKETING_EMAIL", "MARKETING_SMS", "MARKETING_TELEGRAM", "MARKETING_CHAT"]);

const isKind = (value: ConsentRecordDto | ConsentKindDto): value is ConsentKindDto => "history" in value && Array.isArray(value.history);

const newestFirst = (a: ConsentRecordDto, b: ConsentRecordDto) => b.acceptedAt.localeCompare(a.acceptedAt);

/** Ответ API → виды согласий (последняя запись первой); плоский список прежнего API раскладывается здесь. */
export function groupConsents(raw: ReadonlyArray<ConsentRecordDto | ConsentKindDto>): ConsentKindGroupDto[] {
  const kinds = raw.filter(isKind);
  if (kinds.length === raw.length) {
    return kinds.map(({ kind, active, history, ...current }) => {
      // Редакция последней записи — сверху или из той же записи в истории.
      const same = history.find((h) => h.id === current.id);
      const latest: ConsentRecordDto = { ...current, legalRevision: current.legalRevision ?? same?.legalRevision };
      return { kind, active, current: latest, history: history.length > 0 ? [...history].sort(newestFirst) : [latest] };
    });
  }
  const byKind = new Map<ConsentType, ConsentRecordDto[]>();
  for (const record of raw.filter((r): r is ConsentRecordDto => !isKind(r))) {
    byKind.set(record.consentType, [...(byKind.get(record.consentType) ?? []), record]);
  }
  return [...byKind.entries()].map(([kind, records]) => {
    const history = [...records].sort(newestFirst);
    const current = history[0] as ConsentRecordDto;
    return { kind, current, history, active: current.revokedAt === null };
  });
}

/** Источник словами: с сервера или по коду. */
export function consentSourceText(record: Pick<ConsentRecordDto, "source" | "sourceLabel">): string {
  return record.sourceLabel ?? CONSENT_SOURCE_LABEL[record.source] ?? record.source;
}

/**
 * Строка о редакции, если её нет (§3): рассылка — «Отдельного документа о
 * рассылке нет…» (своего документа у неё нет); остальное — согласие дано до
 * начала записи редакций (перенос по дате — `legalRevision.byDate`, его
 * пометку показывает строка редакции). `undefined` — сервер ещё не отдаёт.
 */
export function consentNoRevisionText(record: Pick<ConsentRecordDto, "consentType" | "legalRevision">): string | null {
  if (record.legalRevision !== null) return null;
  if (MARKETING.has(record.consentType)) return "Отдельного документа о рассылке нет — записано по общей политике";
  return "Редакция не записана — согласие дано до начала записи редакций";
}

/** «Согласие на обработку ПДн, ред. 2 от 12.10.2026 · по дате согласия». */
export function consentRevisionText(rev: NonNullable<ConsentRecordDto["legalRevision"]>): string {
  return `${rev.documentTitle}, ред. ${rev.number} от ${formatShopDate(rev.publishedAt)}${rev.byDate ? " · по дате согласия" : ""}`;
}
